import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useManifest } from '../content/useManifest';
import { useNavStore, type AABB } from '../store/useNavStore';
import { PlaceholderObject } from './PlaceholderScene';
import { GltfObject } from './GltfObject';
import { BakedLighting } from './lighting/BakedLighting';
import { SkyDome } from './SkyDome';
import { SceneEnhancements } from './SceneEnhancements';
import { EraGroup } from './EraGroup';
import { VistaGate } from './VistaGate';
import { EraPhotoOverlay } from './EraPhotoOverlay';
import { AudioListenerBinding } from '../audio/AudioListenerBinding';
import { AmbientAudioSources } from '../audio/AmbientAudioSources';
import { DrawCallProbe } from '../perf/drawCallProbe';
import { DebugHUDPerfProbe } from '../perf/DebugHUDPerfPanel';
import { FpsGuardProbe } from '../perf/fpsGuard';
import { VistaPermalinkRig } from '../permalink/VistaPermalinkRig';
import type { ManifestObject } from '../content/schema';

/**
 * Derive an axis-aligned bounding box from a ManifestObject's placeholder.
 *
 * For plane geometry, `size[1]` is the plane's Y "thickness" — since planes are
 * infinitely thin in three, we give them a nominal 1m collision height so the
 * walk controller can't clip through the ground.
 *
 * This is the standard Minkowski pre-step: Plan 03's collision resolver inflates
 * each AABB by PLAYER_RADIUS at resolve-time, so this function doesn't add padding.
 */
function aabbFromPlaceholder(obj: ManifestObject): AABB | null {
  if (!obj.placeholder) return null;
  const [sx, sy, sz] = obj.placeholder.size;
  const [px, py, pz] = obj.position;
  const hx = sx / 2;
  const hy = obj.placeholder.geometry === 'plane' ? 0.5 : sy / 2;
  const hz = sz / 2;
  return {
    min: [px - hx, py - hy, pz - hz],
    max: [px + hx, py + hy, pz + hz],
  };
}

/**
 * Reads the World Manifest via `useManifest()` and:
 *   1. Partitions manifest.objects into three era groups ('1925' / '2026' / 'shared')
 *   2. Wraps each group in <EraGroup era="..."> — EraGroup drives opacity damping
 *      per Plan 04-02 Wave 1 (no era-filter here; opacity controls visibility)
 *   3. Populates `useNavStore.collisionBoxes` with AABBs for ALL collidable
 *      placeholders, era-agnostic (walls stay solid regardless of era toggle)
 *   4. Populates `useNavStore.outerBoundary` from manifest.outerBoundary
 *
 * Plan 03's WalkRig consumes collision data; Plan 04-02's EraGroup consumes the
 * three era-partitioned children. This component is the single point where
 * manifest data becomes THREE scene graph.
 */
export function SceneGraph() {
  const { manifest, error } = useManifest();
  const setCollisionBoxes = useNavStore((s) => s.setCollisionBoxes);
  const setOuterBoundary = useNavStore((s) => s.setOuterBoundary);

  // Partition objects by era — all three groups always mount; opacity tween
  // controls what's visible (Plan 04-02 Pattern 2).
  const grouped = useMemo(() => {
    const groups: Record<'1925' | '2026' | 'shared', ManifestObject[]> = {
      '1925': [],
      '2026': [],
      shared: [],
    };
    if (manifest) {
      for (const obj of manifest.objects) {
        groups[obj.era].push(obj);
      }
    }
    return groups;
  }, [manifest]);

  // Collision AABBs stay era-agnostic — all collidable placeholders contribute
  // regardless of era. WalkRig does not know about era toggles; walls stay solid.
  const placeholderCollisionAABBs = useMemo(() => {
    if (!manifest) return [];
    return manifest.objects
      .filter((o) => o.placeholder?.collision === true)
      .map(aabbFromPlaceholder)
      .filter((a): a is AABB => a !== null);
  }, [manifest]);

  // GLB-authored object AABBs are reported asynchronously (after the .glb
  // loads and Box3.setFromObject runs). We aggregate them by manifestId so
  // re-renders from a single child don't drop peer AABBs.
  // Pitfall 9 closure: the schema does not carry a `collision` flag for
  // glb-authored objects, so SceneGraph auto-collides every glb except the
  // walkable-ground exclusions coded into GltfObject (terrain + water).
  const [glbAABBs, setGlbAABBs] = useState<Record<string, AABB>>({});
  const handleGlbCollision = useCallback(
    (id: string, aabb: AABB | null) => {
      setGlbAABBs((prev) => {
        // Skip if identical to avoid re-render loops.
        const existing = prev[id];
        if (aabb === null) {
          if (existing === undefined) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        }
        if (
          existing &&
          existing.min[0] === aabb.min[0] &&
          existing.min[1] === aabb.min[1] &&
          existing.min[2] === aabb.min[2] &&
          existing.max[0] === aabb.max[0] &&
          existing.max[1] === aabb.max[1] &&
          existing.max[2] === aabb.max[2]
        ) {
          return prev;
        }
        return { ...prev, [id]: aabb };
      });
    },
    [],
  );

  // Merged collision set — placeholders + glb-authored objects. Published
  // to useNavStore on every change so WalkRig sees live updates.
  const mergedCollisionAABBs = useMemo(
    () => [...placeholderCollisionAABBs, ...Object.values(glbAABBs)],
    [placeholderCollisionAABBs, glbAABBs],
  );

  useEffect(() => {
    setCollisionBoxes(mergedCollisionAABBs);
  }, [mergedCollisionAABBs, setCollisionBoxes]);

  useEffect(() => {
    if (manifest) setOuterBoundary(manifest.outerBoundary);
  }, [manifest, setOuterBoundary]);

  if (error) {
    console.error('[SceneGraph] manifest load error:', error);
    return null;
  }

  return (
    <>
      <BakedLighting />
      {/* SkyDome: stylized hemispherical gradient (Phase 8 polish). Back-side
          rendered sphere parented to camera; warm-ochre horizon -> slate-blue
          zenith. Replaces the flat `<color attach="background">` in Renderer.
          renderOrder=-1 so it always draws behind everything. */}
      <SkyDome />
      {/* Phase 8 continuation — subtle non-shader visual enhancements:
          lake shimmer bars (Rule 8-compliant static ripple suggestion),
          near-ground warm-ochre mist, far-horizon atmospheric haze. Gives
          the scene more character without PBR / shaders / wave sim. */}
      <SceneEnhancements />
      {/* AudioListenerBinding: mount THREE.AudioListener on the active
          camera EXACTLY ONCE (Plan 05-05 Task 1, Pitfall 4 closure).
          MUST be mounted BEFORE any PositionalAudio descendants so the
          listener exists when drei's internal "attach if missing" check
          runs. Returns null. */}
      <AudioListenerBinding />
      {/* AmbientAudioSources: manifest-driven drei PositionalAudio
          renderer (Plan 05-05 Task 2, AUDIO-01 + AUDIO-02). Gated on
          navState !== 'idle' so no autoplay before user gesture
          (BoardingOverlay click satisfies autoplay policy). */}
      <AmbientAudioSources />
      {/* VistaGate: side-effect-only camera-in-zone detector + auto-era-flip
          (Plan 04-04 Task 2). Mounted inside Canvas; returns null. */}
      <VistaGate />
      {/* EraPhotoOverlay: Billboard plane rendering the active vista's 2026
          photograph with opacity damp (Plan 04-04 Task 3). Gated internally;
          emits nothing while era='1925' or currentVistaId=null. */}
      <EraPhotoOverlay />
      {/* Plan 07-05 PERF-03 — exposes window.__rendererInfo() only when the URL
          carries ?probe=1 (Playwright perf spec). Inert otherwise. */}
      <DrawCallProbe />
      {/* Plan 07-05 dev panel — samples gl.info into usePerfStore every frame
          when ?debug=1. DebugHUD reads the slice via DebugHUDPerfReadout. */}
      <DebugHUDPerfProbe />
      {/* Plan 07-05 runtime FPS guard probe — samples frame time; flips a
          Zustand flag when sustained median FPS drops below 40 for 3s. The
          DOM toast (FpsGuardToast) lives in UIShell and subscribes to the flag. */}
      <FpsGuardProbe />
      {/* Plan 07-06 HOST-04 — reads #v/#c/#e hash on mount to restore vista +
          era; writes debounced 500ms hash from current camera + era every
          frame. Canvas-scope host (useThree + useFrame). Returns null. */}
      <VistaPermalinkRig />
      <EraGroup era="shared">
        {grouped.shared.map((obj) => (
          <ManifestObjectRenderer
            key={obj.id}
            obj={obj}
            onGlbCollision={handleGlbCollision}
          />
        ))}
      </EraGroup>
      <EraGroup era="1925">
        {grouped['1925'].map((obj) => (
          <ManifestObjectRenderer
            key={obj.id}
            obj={obj}
            onGlbCollision={handleGlbCollision}
          />
        ))}
      </EraGroup>
      <EraGroup era="2026">
        {grouped['2026'].map((obj) => (
          <ManifestObjectRenderer
            key={obj.id}
            obj={obj}
            onGlbCollision={handleGlbCollision}
          />
        ))}
      </EraGroup>
    </>
  );
}

/**
 * Renders a ManifestObject via either the legacy PlaceholderObject (inline
 * geometry) or the Phase 6 GltfObject (.glb-authored). The `model` field
 * on the manifest object is the discriminator.
 *
 * GltfObject is wrapped in <Suspense> because useGLTF suspends while the
 * .glb loads. A null fallback keeps the scene graph consistent; individual
 * assets pop in as they arrive. Collision AABBs are reported after the
 * subtree mounts (via onCollisionReady) so WalkRig sees them the moment
 * the geometry becomes interactable.
 */
function ManifestObjectRenderer({
  obj,
  onGlbCollision,
}: {
  obj: ManifestObject;
  onGlbCollision: (id: string, aabb: AABB | null) => void;
}) {
  if (obj.model) {
    return (
      <Suspense fallback={null}>
        <GltfObject obj={obj} onCollisionReady={onGlbCollision} />
      </Suspense>
    );
  }
  return <PlaceholderObject obj={obj} />;
}
